import { useTheme } from "@mui/material";
import { type RefObject, useEffect } from "react";
import { isAndroidApp } from "web_myplann/Utils/device";

const THUMB_MIN_HEIGHT_PX = 32;
const FADE_OUT_DELAY_MS = 700;

export const hideNativeScrollbarSx = isAndroidApp
	? { "&::-webkit-scrollbar": { display: "none", width: 0, height: 0 } }
	: {};

export const useOverlayScrollbar = (
	scrollerRef: RefObject<HTMLElement | null>,
): void => {
	const theme = useTheme();
	const { width, inset, color, radius, fadeDuration } = theme.overlayScrollbar;

	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!isAndroidApp || !scroller) return;

		const previousPosition = scroller.style.position;
		if (getComputedStyle(scroller).position === "static") {
			scroller.style.position = "relative";
		}

		const thumb = document.createElement("div");
		Object.assign(thumb.style, {
			position: "absolute",
			right: `${inset}px`,
			top: "0",
			width: `${width}px`,
			borderRadius: `${radius}px`,
			backgroundColor: color,
			opacity: "0",
			transition: `opacity ${fadeDuration}ms ease-out`,
			pointerEvents: "none",
			willChange: "transform, opacity",
		} satisfies Partial<CSSStyleDeclaration>);
		scroller.appendChild(thumb);

		let fadeOutTimeout: ReturnType<typeof setTimeout> | undefined;
		let frame: number | undefined;

		const render = () => {
			frame = undefined;
			const { scrollHeight, clientHeight, scrollTop } = scroller;
			const scrollableDistance = scrollHeight - clientHeight;
			if (scrollableDistance <= 0) {
				thumb.style.opacity = "0";
				return;
			}

			const height = Math.max(
				THUMB_MIN_HEIGHT_PX,
				(clientHeight / scrollHeight) * clientHeight,
			);
			const travel = clientHeight - height;
			const offset = (scrollTop / scrollableDistance) * travel;

			thumb.style.height = `${height}px`;
			thumb.style.transform = `translateY(${scrollTop + offset}px)`;
		};

		const scheduleRender = () => {
			frame ??= requestAnimationFrame(render);
		};

		const handleScroll = () => {
			thumb.style.opacity = "1";
			scheduleRender();

			clearTimeout(fadeOutTimeout);
			fadeOutTimeout = setTimeout(() => {
				thumb.style.opacity = "0";
			}, FADE_OUT_DELAY_MS);
		};

		scroller.addEventListener("scroll", handleScroll, { passive: true });

		const resizeObserver = new ResizeObserver(scheduleRender);
		resizeObserver.observe(scroller);
		for (const child of scroller.children) {
			if (child !== thumb) resizeObserver.observe(child);
		}

		render();

		return () => {
			clearTimeout(fadeOutTimeout);
			if (frame !== undefined) cancelAnimationFrame(frame);
			resizeObserver.disconnect();
			scroller.removeEventListener("scroll", handleScroll);
			thumb.remove();
			scroller.style.position = previousPosition;
		};
	}, [scrollerRef, width, inset, color, radius, fadeDuration]);
};
